import { AuthLogLevel } from "../auth/cognito-browser";
import { getTenantExtraQueryParameters } from "./tenant-code";

const DEFAULT_OIDC_SCOPES = ["openid", "email", "profile"];

const normalizeDomain = (domain) =>
  String(domain || "")
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");

const parseScopeList = (value, fallback = []) => {
  if (Array.isArray(value)) {
    return value.map((s) => String(s).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return fallback;
};

/**
 * @param {object} tenant — entry from /config/tenants.json
 */
export function buildAuthConfig(tenant) {
  if (!tenant?.clientId || !tenant?.userPoolId || !tenant?.region || !tenant?.domain) {
    throw new Error(
      "Tenant auth config is incomplete (requires clientId, userPoolId, region, domain).",
    );
  }

  const cognitoDomain = normalizeDomain(tenant.domain);
  const oidcScopes = parseScopeList(tenant.oidcScopes, DEFAULT_OIDC_SCOPES);
  const apiScopes = parseScopeList(tenant.apiScopes, []);
  const requestedScopes = [...oidcScopes, ...apiScopes];
  const authority = `https://cognito-idp.${tenant.region}.amazonaws.com/${tenant.userPoolId}`;
  const origin = window.location.origin;

  const cognitoConfig = {
    auth: {
      clientId: tenant.clientId,
      authority,
      cognitoDomain,
      knownAuthorities: [cognitoDomain],
      redirectUri: origin,
      postLogoutRedirectUri: `${origin}/logout`,
      defaultScopes: oidcScopes,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case AuthLogLevel.Error:
              console.error(message);
              return;
            case AuthLogLevel.Info:
              console.info(message);
              return;
            case AuthLogLevel.Verbose:
              console.debug(message);
              return;
            case AuthLogLevel.Warning:
              console.warn(message);
              return;
            default:
              console.log(message);
          }
        },
      },
    },
  };

  const extraQueryParameters = getTenantExtraQueryParameters();

  return {
    cognitoConfig,
    unifiedRequest: { scopes: requestedScopes },
    loginRequest: {
      scopes: requestedScopes,
      extraQueryParameters,
    },
    passwordResetFlow: {
      authority,
      postLogoutRedirectUri: origin,
    },
  };
}
